import type { ArticleModule, ModuleRowColumn } from 'types/capi/article';

import { rendersModule } from '~/utils/moduleImpression';
import { isRowModuleHiddenOnMobile } from '~/utils/moduleVisibility';

export type ModulePositions = Map<ArticleModule, number>;

// A dense, 1-based count of the modules the reader's viewport rendered, in
// render order: top-level modules in order, a module-row's columns column by
// column, module by module. The first module a reader can see is 1 and the
// last is the number of modules on the page, so an ordinal reads as depth with
// no lookup. Whatever takes no box takes no number: a module-row is a layout
// container and is not counted, a module whose renderer returns null is not
// counted, and on a mobile viewport a module a row hides on mobile is not
// counted — so the modules after it move up, and one module can hold a
// different number on a phone and on a desktop. On a phone a row's columns
// also stack in the order the editor gave them for mobile, not the order they
// were authored in, so the walk follows that order there — the same rule
// ModuleRow renders by. A top-level module renders on every viewport whatever
// was authored, so it is always counted. Every module-scoped event on the
// article reads from this one map, which is what keeps the module impressions,
// the cards and embeds inside them and the article view's manifest agreeing on
// a number within one view.
export function getModulePositions(
  modules: ArticleModule[],
  { mobile = false }: { mobile?: boolean } = {}
): ModulePositions {
  const positions: ModulePositions = new Map();
  let next = 1;

  const visit = (module: ArticleModule) => {
    if (!rendersModule(module)) return;

    if (module.type !== 'module-row') {
      positions.set(module, next++);

      return;
    }

    columnsInRenderOrder(module.columns ?? [], mobile).forEach((column) =>
      column.modules?.forEach((childModule) => {
        if (mobile && isRowModuleHiddenOnMobile(column, childModule)) return;

        visit(childModule);
      })
    );
  };

  modules.forEach(visit);

  return positions;
}

function columnsInRenderOrder(columns: ModuleRowColumn[], mobile: boolean) {
  if (!mobile || columns.length < 2) return columns;

  return columns
    .map((column, index) => ({ column, order: column.mobile_order ?? index }))
    .sort((a, b) => a.order - b.order)
    .map(({ column }) => column);
}
